import React, { useMemo, useState } from 'react';
import { ActivityIndicator, ScrollView, TextInput, TouchableOpacity, View } from 'react-native';
import { useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';

import tw from '../../../../styles/tailwind';
import Text from '../../../../components/Text';
import Modal from '../../../../components/Modal';
import Input from '../../../../components/Forms/Input';
import IconButton from '../../../../components/IconButton';
import AccessServiceTypePicker from '../../../../components/AccessServiceTypePicker';
import useFormSubmit from '../../../../hooks/useFormSubmit';
import useActiveAdminCommunity from '../../../../hooks/useActiveAdminCommunity';
import useAccessServiceTypes from '../../../../hooks/useAccessServiceTypes';
import api from '../../../../utils/api';


export default function NewAccessAuthorizationModal({ communityId, onRequestClose, onCreated }: any) {
  const { community } = useActiveAdminCommunity();
  const { catalogEnabled, descriptionMode, types, isLoading: typesLoading } = useAccessServiceTypes(communityId, community?.settings);

  const { control, handleSubmit, watch, setValue } = useForm<any>({
    defaultValues: {
      unit_uuid: '',
      service_type: '',
      service_type_uuid: '',
      description: '',
      access_type: 'vehicular',
      provider_name: '',
      start_date: dayjs().format('YYYY-MM-DD'),
      end_date: '',
      start_time: '',
      end_time: '',
    },
  });

  const [unitSearch, setUnitSearch] = useState('');

  const unitsQuery = useQuery<any>({
    queryKey: ['admin.units.lookup', communityId],
    queryFn: () => api.get(`/communities/${communityId}/units`).then(r => r.data),
    enabled: !!communityId,
  });

  const units: any[] = useMemo(() => {
    const all = unitsQuery.data?.data ?? unitsQuery.data ?? [];
    if (!unitSearch.trim()) return all;
    const q = unitSearch.toLowerCase();
    return all.filter((u: any) => (u.name ?? '').toLowerCase().includes(q));
  }, [unitsQuery.data, unitSearch]);

  const unitUuid = watch('unit_uuid');
  const accessType = watch('access_type');
  const serviceTypeUuid = watch('service_type_uuid');

  const { submitForm, isSubmitting } = useFormSubmit({
    onSuccess: onCreated,
    onError: () => {},
    successToastMessage: 'Autorización creada y avalada',
    endpoint: `/communities/${communityId}/access-authorizations`,
  });

  function onSave(data: any) {
    submitForm({
      unit_uuid: data.unit_uuid,
      ...(catalogEnabled
        ? { service_type_uuid: data.service_type_uuid }
        : { service_type: data.service_type?.trim() || null }),
      description: data.description?.trim() || null,
      access_type: data.access_type,
      provider_name: data.provider_name?.trim() || null,
      start_date: data.start_date,
      end_date: data.end_date || null,
      start_time: data.start_time || null,
      end_time: data.end_time || null,
      auto_approve: true,
    });
  }

  return (
    <Modal onRequestClose={onRequestClose} title="Nueva autorización" style={{ modal: tw`w-[640px]` }}>
      <ScrollView style={tw`max-h-[70vh]`} contentContainerStyle={tw`pt-2 pr-2`}>
        <Text style={tw`text-sm text-gray-600 mb-2`}>Vivienda</Text>
        <View style={tw`flex-row items-center bg-gray-50 border border-gray-200 rounded-full px-4 py-2 mb-2`}>
          <TextInput
            value={unitSearch}
            onChangeText={setUnitSearch}
            placeholder='Buscar vivienda'
            placeholderTextColor='#9ca3af'
            style={tw`flex-1 text-sm text-gray-900`}
          />
        </View>
        <View style={tw`max-h-40 border border-gray-200 rounded-lg mb-5`}>
          {unitsQuery.isLoading && <View style={tw`p-4 items-center`}><ActivityIndicator /></View>}
          <ScrollView>
            {units.slice(0, 40).map((u: any) => (
              <TouchableOpacity
                key={u.uuid}
                onPress={() => setValue('unit_uuid', u.uuid)}
                style={tw.style(`px-3 py-2 border-b border-gray-100 flex-row justify-between`, unitUuid === u.uuid && `bg-primary/5`)}
              >
                <Text style={tw.style(`text-gray-700`, unitUuid === u.uuid && `text-primary font-600`)}>{u.name}</Text>
                {unitUuid === u.uuid && <Text style={tw`text-primary`}>✓</Text>}
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>

        {catalogEnabled ? (
          <>
            <Text style={tw`text-sm text-gray-600 mb-2`}>Tipo de servicio</Text>
            <AccessServiceTypePicker
              types={types}
              value={serviceTypeUuid}
              onChange={(uuid) => setValue('service_type_uuid', uuid)}
              isLoading={typesLoading}
            />
          </>
        ) : (
          <>
            <Text style={tw`text-sm text-gray-600 mb-1`}>Tipo de servicio (opcional)</Text>
            <Input control={control} name='service_type' placeholder='Ej: Mudanza, Plomería…' />
          </>
        )}

        {descriptionMode !== 'off' && (
          <>
            <Text style={tw`text-sm text-gray-600 mb-1`}>Descripción{descriptionMode === 'optional' ? ' (opcional)' : ''}</Text>
            <Input control={control} name='description' rules={{ required: descriptionMode === 'required' }} multiline numberOfLines={3} />
          </>
        )}

        <Text style={tw`text-sm text-gray-600 mb-2`}>Tipo de acceso</Text>
        <View style={tw`flex-row gap-2 mb-5`}>
          {[
            { key: 'vehicular', label: 'Vehicular' },
            { key: 'pedestrian', label: 'Peatonal' },
          ].map(opt => (
            <TouchableOpacity
              key={opt.key}
              onPress={() => setValue('access_type', opt.key)}
              style={tw.style(`px-4 py-2 rounded-full border`, accessType === opt.key ? 'border-transparent bg-primary' : 'border-gray-200 bg-white')}
            >
              <Text style={tw.style(`text-xs font-500`, accessType === opt.key ? 'text-white' : 'text-gray-700')}>{opt.label}</Text>
            </TouchableOpacity>
          ))}
        </View>

        <Text style={tw`text-sm text-gray-600 mb-1`}>Nombre del proveedor (opcional)</Text>
        <Input control={control} name='provider_name' placeholder='Ej: Mudanzas López' />

        <View style={tw`flex-row gap-4`}>
          <View style={tw`flex-1`}>
            <Text style={tw`text-sm text-gray-600 mb-1`}>Desde</Text>
            <Input control={control} name='start_date' rules={{ required: true }} placeholder='YYYY-MM-DD' />
          </View>
          <View style={tw`flex-1`}>
            <Text style={tw`text-sm text-gray-600 mb-1`}>Hasta (opcional)</Text>
            <Input control={control} name='end_date' placeholder='YYYY-MM-DD' />
          </View>
        </View>

        <View style={tw`flex-row gap-4`}>
          <View style={tw`flex-1`}>
            <Text style={tw`text-sm text-gray-600 mb-1`}>Hora inicio (opcional)</Text>
            <Input control={control} name='start_time' placeholder='HH:mm' />
          </View>
          <View style={tw`flex-1`}>
            <Text style={tw`text-sm text-gray-600 mb-1`}>Hora fin (opcional)</Text>
            <Input control={control} name='end_time' placeholder='HH:mm' />
          </View>
        </View>

        <IconButton
          disabled={isSubmitting || !unitUuid || (catalogEnabled && !serviceTypeUuid)}
          title='Crear y avalar autorización'
          style={tw`self-end mt-3 mb-2`}
          onPress={handleSubmit(onSave)}
        />
      </ScrollView>
    </Modal>
  );
}
